/** 挂载图片变形预览与确认工具栏，依赖会话状态及 Canvas 平移、缩放事件 */
import './deformation-img.less';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { BaseStagePlugin, useFramework, useScale } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { isHotKeyPress } from '@/utils/event-kit';
import { PluginType, type IExportPlugin } from '@/version-types';
import DeformationImgWrap from './components/deformation-img-wrap';
import { DeformationImgPlugin } from './deformation-img';
import deformationImgPluginState, { useDeformationImgState } from './states/deformation-img';
import { EDeformationEvent } from './types';

const DeformationImgPluginView = React.memo(() => {
    const { $t } = getLocaleStore();
    const framework = useFramework();
    const scale = useScale();
    const isOperation = useDeformationImgState()((state) => state.isOperation);
    const isDeformationImage = useDeformationImgState()((state) => state.isDeformationImage);
    const currentLayer = useDeformationImgState()((state) => state.currentLayer);
    const isSaving = useDeformationImgState()((state) => state.isSaving);
    const nodeRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const wheelY = useRef(0);

    useEffect(() => {
        const node = nodeRef.current;
        if (!isDeformationImage || !node) return;
        const mouseWheelHandle = (ev: WheelEvent) => {
            if (!ev.ctrlKey && !isHotKeyPress()) return;
            ev.preventDefault();
            ev.stopPropagation();
            wheelY.current += ev.deltaY;
            if (Math.abs(wheelY.current) < 4) return;
            wheelY.current = 0;
            if (ev.deltaY > 0) framework.service.edgelessCanvas.zoomOut();
            else if (ev.deltaY < 0) framework.service.edgelessCanvas.zoomIn();
        };
        node.addEventListener('wheel', mouseWheelHandle, { passive: false });
        return () => node.removeEventListener('wheel', mouseWheelHandle);
    }, [isDeformationImage, framework]);

    useLayoutEffect(() => {
        if (!isDeformationImage) return;
        const syncToolbar = () => {
            const toolbar = toolbarRef.current;
            const points = nodeRef.current?.querySelectorAll('.c-deformation-img-operate-point');
            if (!toolbar || !points?.length) return;
            const rects = Array.from(points, (point) => point.getBoundingClientRect());
            const minX = Math.min(...rects.map((rect) => rect.left));
            const minY = Math.min(...rects.map((rect) => rect.top));
            const maxY = Math.max(...rects.map((rect) => rect.bottom));
            toolbar.style.left = `${minX}px`;
            toolbar.style.top = `${minY - 50 < 54 ? maxY + 20 : minY - 50}px`;
        };
        syncToolbar();
        const { edgelessCanvas, events } = framework.service;
        const panHandle = edgelessCanvas.onPan(syncToolbar);
        const zoomHandle = edgelessCanvas.onZoom(syncToolbar);
        return () => {
            events.off(panHandle);
            events.off(zoomHandle);
        };
    }, [framework, isDeformationImage, isOperation, isSaving, scale]);

    if (!isDeformationImage) return null;
    return (
        <div className="deformation-img-plugin-wrap" ref={nodeRef}>
            {!isOperation && !isSaving && (
                <div className="l-operate-stage_confirm" ref={toolbarRef} style={{ position: 'fixed' }}>
                    <div className="btn-cancel" onClick={() => framework.service.events.emit(EDeformationEvent.CANCEL)}>
                        <IconFont name="CrossCircle" fontSize={16} color="var(--content-system-primary, #1C1D1F)" />
                        <div>{$t((v) => v.collapse.adjustCollapse.cancelText)}</div>
                    </div>
                    <div
                        className="btn-confirm"
                        onClick={() => framework.service.events.emit(EDeformationEvent.INNER_CONFIRM)}
                    >
                        <IconFont name="CheckCircle" fontSize={16} color="var(--content-tag)" />
                        <div>{$t((v) => v.collapse.adjustCollapse.okText)}</div>
                    </div>
                </div>
            )}
            <BaseStagePlugin name="deformation-img-plugin">
                <DeformationImgWrap key={currentLayer?.id} scale={scale} framework={framework} />
            </BaseStagePlugin>
        </div>
    );
});

DeformationImgPluginView.displayName = 'DeformationImgPluginView';

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'deformation-img-plugin',
    render() {
        return <DeformationImgPluginView />;
    },
    createState() {
        return [deformationImgPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'DeformationImgPlugin', value: new DeformationImgPlugin(identity) }];
    },
    destroy({ getInstance }) {
        getInstance<DeformationImgPlugin>('DeformationImgPlugin').destroy();
    },
};

export default exportPlugin;
